/** Helpers for tests from business documents (pages/Requirements.tsx); pure so they can be tested. */
import { findPlaceholderRefs, isBuiltin, REQUIREMENT_LIMITS, type RequirementPriority, type RequirementSetStatus, type TestDraft } from '@1qa/shared';

export const PRIORITY_LABEL: Record<RequirementPriority, string> = { HIGH: 'High', MEDIUM: 'Medium', LOW: 'Low' };
export const PRIORITY_TONE = { HIGH: 'danger', MEDIUM: 'warning', LOW: 'muted' } as const;

export type WizardStep = 'documents' | 'requirements' | 'drafts' | 'save';

/** Where a set opens: the first step that still needs work. */
export function stepForStatus(status: RequirementSetStatus): WizardStep {
  if (status === 'NEW' || status === 'REQUIREMENTS') return 'requirements';
  return 'drafts';
}

export interface MissingData {
  name: string;
  /** Drafts using it without a written value: they can't be saved until the name exists. */
  blocking: string[];
  /** Drafts that give a written value: saving works, the written value is used until test data has it. */
  written: string[];
}

/** Test data names the (not rejected, unsaved) drafts use that the project has no test data for. */
export function missingDataOf(drafts: TestDraft[], dataKeys: Record<string, boolean>): MissingData[] {
  const out = new Map<string, MissingData>();
  for (const d of drafts) {
    if (d.status === 'rejected' || d.savedTestId) continue;
    for (const step of d.steps) {
      for (const ref of findPlaceholderRefs(step.value)) {
        if (isBuiltin(ref.name) || Object.hasOwn(dataKeys, ref.name)) continue;
        const m = out.get(ref.name) ?? { name: ref.name, blocking: [], written: [] };
        const list = ref.written !== undefined ? m.written : m.blocking;
        if (!list.includes(d.name)) list.push(d.name);
        out.set(ref.name, m);
      }
    }
  }
  return [...out.values()].sort((a, b) => b.blocking.length - a.blocking.length || a.name.localeCompare(b.name));
}

/** A draft that would be refused on save (missing test data without written values). */
export function draftBlocked(draft: TestDraft, dataKeys: Record<string, boolean>): string[] {
  const names = new Set<string>();
  for (const step of draft.steps)
    for (const ref of findPlaceholderRefs(step.value)) if (ref.written === undefined && !isBuiltin(ref.name) && !Object.hasOwn(dataKeys, ref.name)) names.add(ref.name);
  return [...names];
}

/** Reads files for upload: base64 without the data: prefix. Refuses types and sizes the API refuses. */
export async function filesForUpload(files: File[]): Promise<{ name: string; data: string }[]> {
  if (files.length > REQUIREMENT_LIMITS.files) throw new Error(`Upload at most ${REQUIREMENT_LIMITS.files} documents at a time.`);
  return Promise.all(
    files.map(async (f) => {
      if (f.size > REQUIREMENT_LIMITS.fileBytes) throw new Error(`${f.name} is larger than ${REQUIREMENT_LIMITS.fileBytes / 1024 / 1024} MB.`);
      const bytes = new Uint8Array(await f.arrayBuffer());
      let bin = '';
      for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
      return { name: f.name, data: btoa(bin) };
    }),
  );
}

/** "Checkout BRD v2.pdf" -> "Checkout BRD v2". */
export function nameFromFile(fileName: string): string {
  return fileName.replace(/\.[^.]+$/, '').replace(/[_]+/g, ' ').trim().slice(0, 200);
}

export const newStepId = () => (crypto.randomUUID ? crypto.randomUUID() : `s${Date.now()}${Math.random().toString(36).slice(2)}`);
